import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Node, useEditor } from '@splashthat/splash-cms-engine';
import { QueryClientProvider, QueryClient } from 'react-query';
import TouchpointContainer, {
    SmoothScrollBehavior,
} from 'common/components/TouchpointEditor/components/TouchpointContainer';
import Block from 'common/components/TouchpointEditor/components/TouchpointNodes/Block';
import SpeakerModal from 'common/components/TouchpointEditor/components/TouchpointNodes/SpeakerModal';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { AttendeeSessionProvider } from 'common/components/TouchpointEditor/contexts/AttendeeSessionContext';
import BreakpointPreviewer from 'common/components/TouchpointEditor/components/BreakpointPreviewer';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';

import EventTitleHeadline from 'common/components/TouchpointEditor/components/TouchpointNodes/Headline/SubTypes/EventTitleHeadline/EventTitleHeadline';

import { transformStructureDataForEditor } from '../../lib/transformStructureDataForEditor';

import {
    ActiveSessionGrid,
    VirtualContentBlockContentContainer,
    SpeakerInformationContainer,
} from './Livestream.styles';
import {
    VirtualGuestExperienceContainer,
    FooterBlockContainer,
    LeftLinkContainer,
    RightLinkContainer,
} from '../../components/Common.styles';
import { Sessions } from './Sessions';
import SessionInformation from './nodes/SessionInformation/SessionInformation';
import SpeakerInformation from './nodes/SpeakerInformation/SpeakerInformation';
import LeaveSessionButton from './nodes/Button/SubTypes/LeaveSessionButton/LeaveSessionButton';
import CustomButton from '../../components/nodes/Button/SubTypes/CustomButton';

import ViewEventPageLink from '../../components/nodes/Link/SubTypes/ViewEventPageLink';
import ContactTheOrganizer from '../../components/nodes/Link/SubTypes/ContactTheOrganizer';

import EditorTools from './sidebar';
import Embed from './elements/Embed/Embed';
import ActiveSessionWrapper from './elements/ActiveSessionWrapper';
import ScrollToTop from './elements/ScrollToTop';
import SessionRegistrationAnalytics from './elements/SessionRegistrationAnalytics';

import {
    NODE_ID_VIRTUAL_CONTENT,
    NODE_DISPLAY_NAME_VIRTUAL_CONTENT,
    NODE_ID_EVENT_TITLE,
    NODE_DISPLAY_NAME_EVENT_TITLE,
    NODE_DISPLAY_NAME_SESSION_INFORMATION,
    NODE_DISPLAY_NAME_SPEAKER_INFORMATION,
    NODE_DISPLAY_NAME_LEAVE_BUTTON,
    NODE_ID_SESSION_INFORMATION,
    NODE_ID_SPEAKER_INFORMATION,
    NODE_ID_LEAVE_BUTTON,
    NODE_ID_FOOTER,
    NODE_DISPLAY_NAME_FOOTER,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    NODE_ID_SPEAKER_MODALS,
    NODE_DISPLAY_NAME_SPEAKER_MODALS,
    NODE_ID_CUSTOM_BUTTON_TWO,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
} from './constants';

const queryClient = new QueryClient();

export default function Livestream({ guest, structure, currentPageState }) {
    const { event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });

    const editor = useEditor((state) => ({
        editing: state.editing,
    }));
    const livestreamPageStructure = useMemo(
        () => transformStructureDataForEditor(structure, currentPageState),
        [structure, currentPageState],
    );

    return (
        <QueryClientProvider client={queryClient}>
            <AttendeeSessionProvider eventId={event.id}>
                <SessionRegistrationAnalytics
                    enabled={!editor.editing && !shouldPreview}
                    guestId={guest.id}
                    eventId={event.id}
                >
                    <TouchpointContainer editing={editor.editing}>
                        <BreakpointPreviewer enabled={shouldPreview}>
                            <VirtualGuestExperienceContainer $editing={editor.editing} $previewing={shouldPreview}>
                                {!editor.editing && (
                                    <React.Fragment>
                                        <SmoothScrollBehavior />
                                        <ScrollToTop />
                                    </React.Fragment>
                                )}
                                <Node
                                    is={Block}
                                    id={NODE_ID_VIRTUAL_CONTENT}
                                    displayName={NODE_DISPLAY_NAME_VIRTUAL_CONTENT}
                                    {...livestreamPageStructure[NODE_ID_VIRTUAL_CONTENT]}
                                >
                                    <VirtualContentBlockContentContainer>
                                        <Node
                                            is={EventTitleHeadline}
                                            id={NODE_ID_EVENT_TITLE}
                                            displayName={NODE_DISPLAY_NAME_EVENT_TITLE}
                                            {...livestreamPageStructure[NODE_ID_EVENT_TITLE]}
                                        />
                                        <ActiveSessionWrapper>
                                            <Embed showPlaceholder={editor.editing} guest={guest} />
                                            <ActiveSessionGrid>
                                                <Node
                                                    is={SessionInformation}
                                                    id={NODE_ID_SESSION_INFORMATION}
                                                    displayName={NODE_DISPLAY_NAME_SESSION_INFORMATION}
                                                    {...livestreamPageStructure[NODE_ID_SESSION_INFORMATION]}
                                                />
                                                <SpeakerInformationContainer>
                                                    <Node
                                                        is={SpeakerInformation}
                                                        id={NODE_ID_SPEAKER_INFORMATION}
                                                        displayName={NODE_DISPLAY_NAME_SPEAKER_INFORMATION}
                                                        {...livestreamPageStructure[NODE_ID_SPEAKER_INFORMATION]}
                                                    />
                                                    <Node
                                                        is={LeaveSessionButton}
                                                        id={NODE_ID_LEAVE_BUTTON}
                                                        displayName={NODE_DISPLAY_NAME_LEAVE_BUTTON}
                                                        {...livestreamPageStructure[NODE_ID_LEAVE_BUTTON]}
                                                    />
                                                </SpeakerInformationContainer>
                                            </ActiveSessionGrid>
                                        </ActiveSessionWrapper>
                                    </VirtualContentBlockContentContainer>
                                </Node>
                                <Sessions structure={livestreamPageStructure} />
                                <Node
                                    is={Block}
                                    id={NODE_ID_FOOTER}
                                    displayName={NODE_DISPLAY_NAME_FOOTER}
                                    {...livestreamPageStructure[NODE_ID_FOOTER]}
                                >
                                    <FooterBlockContainer>
                                        <LeftLinkContainer>
                                            <li>
                                                <Node
                                                    is={ContactTheOrganizer}
                                                    id={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                                                    displayName={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                                                    {...livestreamPageStructure[NODE_ID_CONTACT_ORGANIZER_BUTTON]}
                                                />
                                            </li>
                                            <li>
                                                <Node
                                                    is={ViewEventPageLink}
                                                    id={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                                                    displayName={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                                                    {...livestreamPageStructure[NODE_ID_VISIT_EVENT_PAGE_BUTTON]}
                                                />
                                            </li>
                                        </LeftLinkContainer>
                                        <RightLinkContainer>
                                            <Node
                                                is={CustomButton}
                                                id={NODE_ID_CUSTOM_BUTTON}
                                                displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                                                {...livestreamPageStructure[NODE_ID_CUSTOM_BUTTON]}
                                            />
                                            <Node
                                                is={CustomButton}
                                                id={NODE_ID_CUSTOM_BUTTON_TWO}
                                                displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                                                {...livestreamPageStructure[NODE_ID_CUSTOM_BUTTON_TWO]}
                                            />
                                        </RightLinkContainer>
                                    </FooterBlockContainer>
                                </Node>
                                <Node
                                    is={SpeakerModal}
                                    displayName={NODE_DISPLAY_NAME_SPEAKER_MODALS}
                                    id={NODE_ID_SPEAKER_MODALS}
                                    {...livestreamPageStructure[NODE_ID_SPEAKER_MODALS]}
                                />
                            </VirtualGuestExperienceContainer>
                        </BreakpointPreviewer>
                        {editor.editing && <EditorTools />}
                    </TouchpointContainer>
                </SessionRegistrationAnalytics>
            </AttendeeSessionProvider>
        </QueryClientProvider>
    );
}

Livestream.propTypes = {
    structure: PropTypes.object,
    currentPageState: PropTypes.string.isRequired,
    guest: PropTypes.object,
};

Livestream.defaultProps = {
    structure: null,
    guest: {},
};
